import { TrashIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { FormWrapper } from '@/form-control'
import { SelectField, TextField, ToggleField } from '@/form-control/fields'
import ROUTES from '@/ROUTES'

import { IOrganization } from '@/MODELS/organization.model'
import { IStaffUser, OrganizationRole } from '@/MODELS/staffUser.model'

import { api, api_delete, api_patch } from '@/services/api.service'
import { useResource } from '@/services/resource.service'

import AdminPageHeader from '@/components/AdminPageHeader'
import Button from '@/components/Button'
import ConfirmModal from '@/components/ConfirmModal'

const TYPE_OPTIONS = [
    { text: 'DD Board', value: 'dd_board' },
    { text: 'Early Intervention Agency', value: 'early_intervention' },
    { text: 'Clinic', value: 'clinic' },
    { text: 'School', value: 'school' },
    { text: 'Nonprofit', value: 'nonprofit' },
    { text: 'Other', value: 'other' },
]

const STATUS_OPTIONS = [
    { text: 'Active', value: 'active' },
    { text: 'Inactive', value: 'inactive' },
]

const ROLE_OPTIONS: { text: string; value: OrganizationRole; description: string }[] = [
    { text: 'Admin', value: 'admin', description: 'Full control — manages org settings, seats, all families, and team members.' },
    { text: 'Coordinator', value: 'coordinator', description: 'Works with their assigned families only; cannot invite families or manage seats.' },
    { text: 'Read-Only Admin', value: 'viewer', description: 'Read-only access to all families and analytics; cannot take action.' },
]

export default function AdminOrganizationForm() {
    const params = useParams()
    const navigate = useNavigate()
    const id = params?.id ?? 'new'
    const isNew = id === 'new'
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

    async function handleDelete() {
        await api_delete(`${ROUTES.admin.organization}/${id}`)
        navigate('/admin/organizations')
    }

    return (
        <div className='flex flex-col w-full max-w-4xl gap-6 py-10 mx-auto'>
            <div className='flex items-center justify-between'>
                <AdminPageHeader heading={isNew ? 'Create Organization' : 'Edit Organization'} />
                {!isNew && (
                    <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className='inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors'
                    >
                        <TrashIcon className='w-4 h-4' />
                        Delete org
                    </button>
                )}
            </div>

            <FormWrapper<IOrganization>
                endpoint={ROUTES.admin.organization}
                id={id}
                redirectAfterSubmit='/admin/organizations'
                defaultValues={{ type: 'other', status: 'active', seatQuota: 0 }}
                additionalSubmissionRowContent={
                    <div className='flex items-center justify-between w-full gap-x-2 h-fit'>
                        <Button.Back />
                    </div>
                }
            >
                {(f) => (
                    <div className='overflow-hidden bg-white border border-gray-200 shadow-sm rounded-xl'>
                        <div className='p-6 space-y-6'>
                            <div className='grid gap-6 lg:grid-cols-2'>
                                <TextField {...f('name')} label='Organization Name' />
                                <TextField
                                    {...f('displayName')}
                                    label='Name shown to families'
                                    placeholder='Defaults to organization name'
                                />
                                <TextField {...f('contactName')} label='Contact Name' />
                                <TextField {...f('contactEmail')} label='Contact Email' />
                                <SelectField {...f('type')} label='Type' options={TYPE_OPTIONS} />
                                <SelectField {...f('status')} label='Status' options={STATUS_OPTIONS} />
                                <TextField {...f('seatQuota')} label='Seat Quota' inputMode='numeric' />
                            </div>
                            <div className='pt-2 border-t border-gray-100'>
                                <ToggleField
                                    {...f('requireRoadmapReview')}
                                    label='Require roadmap review before showing to family?'
                                />
                                <p className='mt-1 text-sm text-gray-500'>
                                    When turned on, the provider has the opportunity to review/approve all roadmaps
                                    before their clients see them. Leave off for the standard experience where families
                                    get their AI-generated roadmaps right after onboarding.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </FormWrapper>

            {!isNew && (
                <>
                    <div className='flex items-center gap-4 mt-2'>
                        <div className='flex-1 border-t border-gray-200' />
                        <span className='text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap'>Team Management</span>
                        <div className='flex-1 border-t border-gray-200' />
                    </div>
                    <CoordinatorsSection orgId={id} />
                </>
            )}

            <ConfirmModal
                open={showDeleteConfirm}
                onCancel={() => setShowDeleteConfirm(false)}
                onConfirm={handleDelete}
                title='Delete organization'
                description='Deletes this organization. All coordinator accounts will be disabled and their emails freed up. Family licenses are kept for audit but families revert to their normal access. This cannot be undone.'
                confirmText='Delete'
                cancelText='Cancel'
                intent='danger'
            />
        </div>
    )
}

const ROLE_BADGE_CLS: Record<OrganizationRole, string> = {
    admin: 'bg-blue-50 text-blue-700',
    coordinator: 'bg-teal-50 text-teal-700',
    viewer: 'bg-neutral-100 text-neutral-600',
}

// Lives below the org form. Has its own fetch so it stays in sync with adds /
// removals without needing to re-render the FormWrapper above.
function CoordinatorsSection({ orgId }: { orgId: string }) {
    const endpoint = `${ROUTES.admin.organization}/${orgId}`
    type OrgDetail = IOrganization & { members: IStaffUser[] }
    const [orgDetail, , orgResource] = useResource<OrgDetail>(endpoint)

    const [email, setEmail] = useState('')
    const [name, setName] = useState('')
    const [role, setRole] = useState<OrganizationRole>('coordinator')
    const [error, setError] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)
    const [removeId, setRemoveId] = useState<string | null>(null)
    const [lastAddInfo, setLastAddInfo] = useState<{ text: string; type: 'success' | 'info' } | null>(null)
    const [roleChangingId, setRoleChangingId] = useState<string | null>(null)

    async function handleAdd() {
        setError(null)
        setLastAddInfo(null)
        if (!email.trim()) {
            setError('Email is required')
            return
        }
        setSubmitting(true)
        try {
            const response = await api<{ message: string; createdNewAccount: boolean }>(
                `${endpoint}/member`,
                { email: email.trim(), name: name.trim() || undefined, role }
            )
            if (response.status && response.status >= 400) {
                setError(response.error || 'Failed to add coordinator')
            } else {
                setEmail('')
                setName('')
                setRole('coordinator')
                if (response.data?.createdNewAccount) {
                    setLastAddInfo({ text: 'Account created. They were emailed a link to set their password.', type: 'success' })
                } else {
                    setLastAddInfo({ text: 'Added. This email already has an account — no activation email was sent. They can log in to the provider portal using their existing credentials.', type: 'info' })
                }
                await orgResource.get()
            }
        } catch (e: any) {
            setError(e?.error || 'Failed to add coordinator')
        } finally {
            setSubmitting(false)
        }
    }

    async function handleRoleChange(memberId: string, newRole: OrganizationRole) {
        setError(null)
        setRoleChangingId(memberId)
        const response = await api_patch(`${endpoint}/member/${memberId}`, { role: newRole })
        if (response.status && response.status >= 400) {
            setError(response.error || 'Failed to update role')
        } else {
            await orgResource.get()
        }
        setRoleChangingId(null)
    }

    async function handleRemove(memberId: string) {
        setError(null)
        const response = await api_delete(`${endpoint}/member/${memberId}`)
        if (response.status && response.status >= 400) {
            setError(response.error || 'Failed to remove member')
        } else {
            await orgResource.get()
        }
        setRemoveId(null)
    }

    return (
        <div className='overflow-hidden bg-white border border-gray-200 shadow-sm rounded-xl'>
            <div className='px-6 py-4 border-b border-gray-200'>
                <h2 className='text-lg font-medium text-gray-900'>Team members</h2>
                <p className='text-sm text-gray-500'>
                    Add by email and role. New emails get an account with a "set your password" link automatically.
                </p>
            </div>

            {/* Role legend */}
            <div className='px-6 py-4 border-b border-gray-100 bg-neutral-50'>
                <p className='text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2'>Role guide</p>
                <div className='grid gap-1.5 sm:grid-cols-2'>
                    {ROLE_OPTIONS.map((o) => (
                        <div key={o.value} className='flex items-start gap-2'>
                            <span className={`inline-block shrink-0 rounded-full px-2 py-0.5 text-xs font-medium capitalize mt-0.5 ${ROLE_BADGE_CLS[o.value]}`}>
                                {o.text}
                            </span>
                            <span className='text-xs text-neutral-600'>{o.description}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Add form */}
            <div className='p-6 space-y-3 border-b border-gray-200'>
                <div className='grid gap-3 lg:grid-cols-12 items-end'>
                    <div className='lg:col-span-5'>
                        <label className='block mb-1 text-sm font-medium text-gray-700'>Email</label>
                        <input
                            type='email'
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder='coordinator@example.org'
                            className='w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                        />
                    </div>
                    <div className='lg:col-span-4'>
                        <label className='block mb-1 text-sm font-medium text-gray-700'>Name (optional)</label>
                        <input
                            type='text'
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder='Ashley Reed'
                            className='w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                        />
                    </div>
                    <div className='lg:col-span-2'>
                        <label className='block mb-1 text-sm font-medium text-gray-700'>Role</label>
                        <select
                            value={role}
                            onChange={(e) => setRole(e.target.value as OrganizationRole)}
                            className='w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                        >
                            {ROLE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.text}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className='lg:col-span-1'>
                        <Button onClickAsync={handleAdd} isLoading={submitting}>
                            Add
                        </Button>
                    </div>
                </div>
                {error && <p className='text-sm text-red-600'>{error}</p>}
                {lastAddInfo && (
                    <p className={`text-sm ${lastAddInfo.type === 'success' ? 'text-green-700' : 'text-amber-700'}`}>
                        {lastAddInfo.text}
                    </p>
                )}
            </div>

            {/* List */}
            <div className='divide-y divide-gray-100'>
                {(orgDetail?.members ?? []).length === 0 ? (
                    <div className='p-6 text-sm text-gray-500'>No team members yet.</div>
                ) : (
                    (orgDetail?.members ?? []).map((m: any) => (
                        <div key={m._id} className='flex items-center justify-between px-6 py-3 gap-3'>
                            <div className='flex flex-col min-w-0'>
                                <span className='text-sm font-medium text-gray-900 truncate'>{m.name || m.email}</span>
                                <span className='text-xs text-gray-500 truncate'>{m.email}</span>
                            </div>
                            <div className='flex items-center gap-2 shrink-0'>
                                <select
                                    value={m.role}
                                    disabled={roleChangingId === m._id}
                                    onChange={(e) => handleRoleChange(m._id, e.target.value as OrganizationRole)}
                                    title={ROLE_OPTIONS.find((o) => o.value === m.role)?.description}
                                    className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize border-0 cursor-pointer focus:ring-2 focus:ring-blue-500 disabled:opacity-50 ${ROLE_BADGE_CLS[m.role as OrganizationRole] ?? 'bg-neutral-100 text-neutral-600'}`}
                                >
                                    {ROLE_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.text}</option>
                                    ))}
                                </select>
                                <button
                                    onClick={() => setRemoveId(m._id)}
                                    className='text-gray-400 hover:text-red-600'
                                    title='Remove'
                                >
                                    <TrashIcon className='w-5 h-5' />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <ConfirmModal
                open={!!removeId}
                onCancel={() => setRemoveId(null)}
                onConfirm={() => removeId && handleRemove(removeId)}
                title='Remove team member'
                description='Removes their access. If this is their only role, the account is disabled but kept in the database for audit.'
                confirmText='Remove'
                cancelText='Cancel'
                intent='danger'
            />
        </div>
    )
}
